import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Button } from "@mui/material";
import { Formik, Form } from "formik";
import Swal from "sweetalert2";

import { validationSchema } from "./helpers/productForm.schema";
import { buildFormData } from "./helpers/productForm.utils";

import ProductBasicFields from "./form/ProductBasicFields";
import ProductImageField from "./form/ProductImageField";
import ProductCategoryFields from "./form/ProductCategoryFields";
import ProductVariationsSection from "./form/ProductVariationsSection";

import { AddProduct, UpdateProduct } from "../../../utils/product";
import { UpdateProductOption, AddProductOption, DeleteProductOption } from "../../../utils/productOptions";
import ProductDealsSection from "./form/ProductDealsSection";

export default function ProductFormDialog({
  open,
  onClose,
  initialValues,
  categories,
  onSaved,
}) {
  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const fd = buildFormData(values);

      if (values.id !== 0) {
        const savedProduct = await UpdateProduct(values.id, fd);
        onSaved?.({ mode: "edit", product: savedProduct });

        if (values.variationType && Array.isArray(values.options)) {
          for (const o of values.options) {
            if (o?.id && o.id !== 0) {
              await UpdateProductOption(o.id, {
                productId: values.id,
                optionType: values.variationType,
                value: (o.value || "").trim(),
                extraPrice: Number(o.extraPrice) || 0,
                quantity: Number(o.quantity) || 0,
                image: o.image instanceof File ? o.image : undefined,
              });
            } else if ((o.value || "").trim() !== "") {
              await AddProductOption({
                productId: values.id,
                optionType: values.variationType,
                value: (o.value || "").trim(),
                extraPrice: Number(o.extraPrice) || 0,
                quantity: Number(o.quantity) || 0,
                image: o.image instanceof File ? o.image : null,
              });
            }
          }
        }

        resetForm();
        onClose();
        document.activeElement?.blur?.();

        setTimeout(() => {
          Swal.fire({
            icon: "success",
            title: "המוצר עודכן",
            timer: 1500,
            showConfirmButton: false,
          });
        }, 0);
      } else {
        const savedProduct = await AddProduct(fd);
        onSaved?.({ mode: "add", product: savedProduct });

        if (values.variationType && Array.isArray(values.options)) {
          const optionsToCreate = values.options.filter(
            (o) => (o.value || "").trim() !== ""
          );

          for (const o of optionsToCreate) {
            await AddProductOption({
              productId: savedProduct.id,
              optionType: values.variationType,
              value: (o.value || "").trim(),
              extraPrice: Number(o.extraPrice) || 0,
              quantity: Number(o.quantity) || 0,
              image: o.image || null,
            });
          }
        }

        resetForm();
        onClose();
        document.activeElement?.blur?.();

        setTimeout(() => {
          Swal.fire({
            icon: "success",
            title: "המוצר נוסף",
            timer: 1500,
            showConfirmButton: false,
          });
        }, 0);
      }
    } catch (error) {
      console.error("Failed to save product:", error);
      Swal.fire({ icon: "error", title: "שמירה נכשלה" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      disableEnforceFocus
      disableRestoreFocus
      disableAutoFocus
    >
      <DialogTitle>{initialValues?.id ? "עריכת מוצר" : "הוסף מוצר"}</DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({
          dirty,
          isValid,
          isSubmitting,
          errors,
          touched,
          setFieldValue,
          values,
          getFieldProps,
        }) => (
          <Form encType="multipart/form-data">
            <DialogContent>
              <Grid container spacing={2}>
                <ProductBasicFields errors={errors} touched={touched} />

                <ProductImageField
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                <ProductCategoryFields
                  categories={categories}
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                <ProductVariationsSection
                  values={values}
                  setFieldValue={setFieldValue}
                  DeleteProductOption={DeleteProductOption}
                />
                <ProductDealsSection
                  values={values}
                  setFieldValue={setFieldValue}
                />
              </Grid>
            </DialogContent>

            <DialogActions>
              {getFieldProps("id").value !== 0 ? (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  שמור עריכת מוצר
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !dirty || !isValid}
                >
                  הוסף מוצר
                </Button>
              )}

              <Button onClick={onClose} disabled={isSubmitting} autoFocus>
                ביטול
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}